import { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import styles from './GodMode.module.css'

// Label formats (migration 211). Dataphyll's catalogue of the formats Control
// can print, each allocated to one client or many. A client's admin picks the
// default for each purpose from the formats allocated to them, and cannot
// switch a format on or off themselves.

export interface CatalogueFormat {
  sku: string
  version: number
  name: string
  description: string | null
  supports_colour: boolean
  supports_purposes: string[]
  supported_optional_fields: string[]
  labels_per_sheet: number
  rows: number
  columns: number
  renderer_key: string
  page_width_mm: number | null
  page_height_mm: number | null
  assigned_only: boolean
}
export interface FormatClient {
  id: string
  name: string
  allocated: string[]
}
export interface LabelFormatsResponse {
  formats: CatalogueFormat[]
  clients: FormatClient[]
}

const message = (e: any, fallback: string) => e?.body?.error || e?.message || fallback

export const formatFacts = (f: CatalogueFormat) => {
  const roll = f.rows === 1 && f.columns === 1
  const prints = f.assigned_only
    ? 'DTU item labels only (an item assigned to a person)'
    : f.supports_purposes.map((p) => (p === 'worker' ? 'Worker IDs' : 'items')).join(' and ')
  return [
    roll ? 'Roll' : `A4 sheet, ${f.labels_per_sheet} labels`,
    f.page_width_mm && f.page_height_mm ? `${f.page_height_mm} × ${f.page_width_mm} mm` : null,
    f.supports_colour ? 'Colour' : 'Black only',
    `Prints ${prints}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function LabelFormatsPage() {
  const { user } = useAuth()
  const [data, setData] = useState<LabelFormatsResponse | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState('')

  useEffect(() => {
    apiJson<LabelFormatsResponse>(user, '/api/god-mode/label-formats')
      .then(setData)
      .catch((e) => setError(message(e, 'Could not load the label formats')))
  }, [user])

  const flip = async (f: CatalogueFormat, c: FormatClient) => {
    if (busy) return
    const allocated = !c.allocated.includes(f.sku)
    const on = `${f.sku}:${c.id}`
    setBusy(on)
    setError(null)
    setNotice('')
    try {
      const r = await apiJson<{ client: FormatClient }>(user, `/api/god-mode/clients/${c.id}/label-formats/${encodeURIComponent(f.sku)}`, {
        method: 'PUT',
        body: { allocated },
      })
      setData((d) => (d ? { ...d, clients: d.clients.map((x) => (x.id === c.id ? { ...x, allocated: r.client.allocated } : x)) } : d))
      setNotice(allocated ? `${f.name} allocated to ${c.name}.` : `${f.name} taken away from ${c.name}. Labels already printed on it keep it.`)
    } catch (e: any) {
      setError(message(e, 'Nothing was changed'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader title="Label formats" />
      <PageBody>
        <div className={styles.stack}>
          <p className={styles.lead}>
            The label formats Control can print. Tick a client to allocate a format to them; their admin then chooses the default for items and for Worker IDs from what is
            allocated. A client cannot switch formats on or off themselves.
          </p>
          {error && <p className={styles.error} role="alert">{error}</p>}
          {notice && <p className={styles.saved} role="status">{notice}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {data?.formats.map((f) => {
            const count = data.clients.filter((c) => c.allocated.includes(f.sku)).length
            return (
              <div key={f.sku} className={styles.card} data-testid="format-card">
                <h2>{f.name}</h2>
                <p className={styles.hint}>
                  {formatFacts(f)} · {f.sku} v{f.version}
                </p>
                {f.description && <p className={styles.lead}>{f.description}</p>}
                <p className={styles.hint}>
                  Allocated to {count} of {data.clients.length} {data.clients.length === 1 ? 'client' : 'clients'}
                </p>
                <div className={styles.chips}>
                  {data.clients.map((c) => (
                    <label key={c.id} className={styles.chip}>
                      <input
                        type="checkbox"
                        checked={c.allocated.includes(f.sku)}
                        disabled={!!busy}
                        aria-label={`${f.name} for ${c.name}`}
                        onChange={() => flip(f, c)}
                      />{' '}
                      {c.name}
                    </label>
                  ))}
                  {!data.clients.length && <span className={styles.hint}>No clients</span>}
                </div>
              </div>
            )
          })}
        </div>
      </PageBody>
    </>
  )
}
